import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';

import { useTitle } from '@/lib/hooks/use-title';
import { useSignUpMutation } from '@/generated/graphql';
import { useGAPageview } from '@/lib/ga/use-ga';
import { User } from '@/auth/types';
import { useAuth } from '@/auth/auth-context';
import { useToastContext } from '@/components/toast/toast-context';

import { Cards } from '../../components/gradient-layout/cards';
import { GoogleSignupCard } from './google-signup-card';
import { EmailSignupCard } from './email-signup-card';
import { FullPageLoader } from '../../components/loader';

import commonStyles from '../public.module.scss';

export const SignupPage = () => {
  useTitle('Sign Up to Supersimple');

  const [signupMutation, { loading: signingUp }] = useSignUpMutation();
  const [email, setEmail] = useState<string | null>(null);
  const navigate = useNavigate();
  const addToast = useToastContext();
  const { logOut, user } = useAuth();
  useGAPageview();

  const signUp = async (user: User, email: string) => {
    try {
      await signupMutation({
        variables: { email, consent: { subjects: [] } },
        refetchQueries: ['Workspace'],
      });

      if (!user.emailVerified) {
        navigate('/verify-email');
        return;
      }
      // Let <Unauthenticated /> handle redirecting to next url or default page
    } catch (error) {
      if ((error as any).message === 'Signup failed: User is already signed up') {
        return navigate('/');
      }

      await logOut();
      addToast({
        title: 'Signup Error',
        content: () => `Something went wrong. Please contact us if the issue persists.`,
        kind: 'error',
      });
      throw error;
    }
  };

  useEffect(() => {
    if (email === null || user === null) {
      return;
    }
    signUp(user, email);
    setEmail(null);
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, user]);

  const handleStartSignup = (email: string) => {
    if (user === null) {
      // In case there is no firebase user yet, schedule signup request after it appears
      setEmail(email);
    } else {
      signUp(user, email);
    }
  };

  if (signingUp) {
    return <FullPageLoader />;
  }

  return (
    <div className={commonStyles.layout}>
      <h1>Get Started!</h1>
      <Cards>
        <GoogleSignupCard onStartSignup={handleStartSignup} />
        <div className={commonStyles.separator}>or</div>
        <EmailSignupCard onStartSignup={handleStartSignup} loading={signingUp} />
      </Cards>
      <p>
        Already have an account? <Link to="/login">Log in</Link>
      </p>
    </div>
  );
};
